import React, { useState } from 'react';
import { Bug, Scan, Zap, ExternalLink, CheckCircle2, Activity, TrendingUp, Eye, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Section } from './InspectionShared';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL;

const MiteSection = ({
  formData, isQuickInspection, handleSectionPhoto, hive,
  treatmentJustStarted, activeTreatments, recentMiteCounts,
  setShowFrameScanDialog, setShowMiteCountDialog, setShowTreatmentWizard,
  token
}) => {
  const [showPlansDialog, setShowPlansDialog] = useState(false);
  const [treatmentHistory, setTreatmentHistory] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const fetchTreatmentHistory = async () => {
    if (!hive?.id || !token) return;
    setLoadingHistory(true);
    try {
      const res = await fetch(`${API}/api/treatment-plans?hive_id=${hive.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setTreatmentHistory(data.plans || []);
    } catch { setTreatmentHistory([]); }
    finally { setLoadingHistory(false); }
  };

  const handleOpenPlans = () => {
    setShowPlansDialog(true);
    if (!treatmentHistory) fetchTreatmentHistory();
  };

  const pastTreatments = (treatmentHistory || [])
    .filter(p => p.status === 'completed' || p.status === 'cancelled')
    .slice(0, 3);

  return (
    <Section 
      title="Mite Monitoring & Assays" 
      icon={<Bug className="h-6 w-6 text-red-600" />}
      collapsible={isQuickInspection}
      defaultCollapsed={isQuickInspection}
      onPhoto={handleSectionPhoto}
    >
      {/* Quick Actions Bar */}
      <div className="flex flex-wrap gap-2 mb-6">
        <Button type="button" onClick={() => setShowFrameScanDialog(true)} className="bg-purple-600 hover:bg-purple-700">
          <Scan className="h-4 w-4 mr-2" /> AI Frame Scanner
        </Button>
        <Button type="button" variant="outline" onClick={() => setShowMiteCountDialog(true)} className="border-amber-400 text-amber-700 hover:bg-amber-50">
          <Bug className="h-4 w-4 mr-2" /> Record Mite Count
        </Button>
        <Button type="button" variant="outline" onClick={() => setShowTreatmentWizard(true)} className="border-red-400 text-red-700 hover:bg-red-50">
          <Zap className="h-4 w-4 mr-2" /> Start Treatment
        </Button>
        <Button type="button" variant="ghost" onClick={handleOpenPlans} className="text-stone-600">
          <Eye className="h-4 w-4 mr-2" /> View Plans
        </Button>
      </div>

      {/* Active Treatment Plans Dialog */}
      <Dialog open={showPlansDialog} onOpenChange={setShowPlansDialog}>
        <DialogContent className="max-w-lg max-h-[70vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5 text-amber-600" /> Treatment Plans for Hive #{hive?.hive_number}
            </DialogTitle>
          </DialogHeader>
          {activeTreatments.length > 0 ? (
            <div className="space-y-3">
              {activeTreatments.map(plan => (
                <div key={plan.id} className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-semibold text-stone-800">{plan.treatment_type_name}</span>
                    <span className="text-xs bg-amber-100 text-amber-800 px-2 py-1 rounded-full font-medium">{plan.status}</span>
                  </div>
                  <div className="text-sm text-stone-500 space-y-0.5">
                    <p>Started: {new Date(plan.start_date).toLocaleDateString()}</p>
                    {plan.end_date && <p>Ends: {new Date(plan.end_date).toLocaleDateString()}</p>}
                    {plan.notes && <p className="text-stone-600 mt-1 italic">{plan.notes}</p>}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Bug className="h-10 w-10 text-stone-200 mx-auto mb-2" />
              <p className="text-stone-500 text-sm">No active treatment plans for this hive.</p>
              <Button type="button" variant="outline" onClick={() => { setShowPlansDialog(false); setShowTreatmentWizard(true); }} className="mt-3 border-red-400 text-red-700 hover:bg-red-50 text-sm">
                <Zap className="h-4 w-4 mr-1" /> Start a Treatment
              </Button>
            </div>
          )}
          {recentMiteCounts.length > 0 && (
            <div className="mt-4 pt-4 border-t border-stone-200">
              <h4 className="text-sm font-semibold text-stone-700 mb-2">Recent Mite Counts</h4>
              <div className="grid grid-cols-3 gap-2">
                {recentMiteCounts.slice(0, 6).map((count, idx) => (
                  <div key={count.id || idx} className={`p-2 rounded-lg text-center ${count.percentage >= 3 ? 'bg-red-100' : 'bg-green-100'}`}>
                    <div className={`text-lg font-bold ${count.percentage >= 3 ? 'text-red-700' : 'text-green-700'}`}>
                      {count.percentage?.toFixed(1)}%
                    </div>
                    <div className="text-[10px] text-stone-500">
                      {new Date(count.date).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Treatment History — last 3 */}
          <div className="mt-4 pt-4 border-t border-stone-200">
            <h4 className="text-sm font-semibold text-stone-700 mb-1">Treatment History (Last 3)</h4>
            <p className="text-[11px] text-stone-400 mb-3">Rotate treatments to avoid resistance build-up.</p>
            {loadingHistory ? (
              <p className="text-xs text-stone-400 text-center py-3">Loading...</p>
            ) : pastTreatments.length > 0 ? (
              <div className="space-y-2">
                {pastTreatments.map((plan, idx) => (
                  <div key={plan.id || idx} className="bg-stone-50 border border-stone-200 rounded-lg p-3">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-sm text-stone-800">{plan.treatment_type_name}</span>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${plan.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-stone-200 text-stone-600'}`}>
                        {plan.status}
                      </span>
                    </div>
                    <div className="text-xs text-stone-500 mt-1 flex flex-wrap gap-x-3">
                      <span>{new Date(plan.start_date).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })} — {plan.end_date ? new Date(plan.end_date).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : 'ongoing'}</span>
                      {plan.duration_days && <span>{plan.duration_days} days</span>}
                    </div>
                    {plan.notes && <p className="text-xs text-stone-500 mt-1 italic">{plan.notes}</p>}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-stone-400 text-center py-3">No past treatments recorded for this hive.</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Treatment Just Started Banner */}
      {treatmentJustStarted && (
        <div className="bg-green-50 border-2 border-green-300 rounded-xl p-4 mb-6" data-testid="treatment-just-started-banner">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
              <CheckCircle2 className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <h4 className="font-semibold text-green-800">Treatment Started During This Inspection</h4>
              <p className="text-sm text-green-700">{treatmentJustStarted} — treatment plan is now active for this hive</p>
            </div>
          </div>
        </div>
      )}

      {/* Active Treatments Alert */}
      {activeTreatments.length > 0 && (
        <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-4 mb-6">
          <h4 className="font-semibold text-amber-800 flex items-center gap-2 mb-2">
            <Activity className="h-5 w-5" /> Active Treatment for This Hive
          </h4>
          {activeTreatments.map(plan => (
            <div key={plan.id} className="flex items-center justify-between bg-white p-3 rounded-lg mb-2">
              <div>
                <span className="font-medium">{plan.treatment_type_name}</span>
                <span className="text-sm text-stone-500 ml-2">
                  Started {new Date(plan.start_date).toLocaleDateString()}
                </span>
              </div>
              <span className="text-xs bg-amber-100 text-amber-800 px-2 py-1 rounded">{plan.status}</span>
            </div>
          ))}
        </div>
      )}

      {/* Recent Mite Counts */}
      {recentMiteCounts.length > 0 && (
        <div className="bg-stone-50 border border-stone-200 rounded-xl p-4 mb-6">
          <h4 className="font-semibold text-stone-700 flex items-center gap-2 mb-3">
            <TrendingUp className="h-5 w-5" /> Recent Mite Counts for #{hive?.hive_number}
          </h4>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            {recentMiteCounts.map((count, idx) => (
              <div key={count.id || idx} className={`p-3 rounded-lg text-center ${count.percentage >= 3 ? 'bg-red-100' : 'bg-green-100'}`}>
                <div className={`text-xl font-bold ${count.percentage >= 3 ? 'text-red-700' : 'text-green-700'}`}>
                  {count.percentage?.toFixed(1)}%
                </div>
                <div className="text-xs text-stone-500">
                  {new Date(count.date).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTreatments.length === 0 && recentMiteCounts.length === 0 && !treatmentJustStarted && (
        <p className="text-sm text-stone-400 text-center py-4">No mite data recorded for this hive yet. Use the buttons above to record a mite count or start a treatment plan.</p>
      )}
    </Section>
  );
};

export default MiteSection;
